'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAppStore } from '@entities/app-store'
import { useFederalDistricts } from '@entities/federal-district'
import { marketplaceLabels, MarketplaceTag, serviceTypes, type FulfillmentApi } from '@entities/fulfillment'
import {
  fulfillmentRequestApi,
  fulfillmentRequestCreateMutation,
  fulfillmentRequestEditMutation,
  messengers,
  packingTypes,
  parseFulfillmentRequestFailureKind,
  requestShipmentTypes,
  type FulfillmentRequestApi,
} from '@entities/fulfillment-request'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Button } from '@ui/base/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/base/card'
import { Checkbox } from '@ui/base/checkbox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Label } from '@ui/base/label'
import { RadioGroup, RadioGroupItem } from '@ui/base/radio-group'
import { Textarea } from '@ui/base/textarea'
import { WizardFooter } from '@ui/wizard-footer'
import { toggleValue } from '@utils/toggle-value'
import { typeClass } from '@utils/typography'
import axios from 'axios'
import { observer } from 'mobx-react-lite'
import { useForm, type FieldPath, type FieldPathValue } from 'react-hook-form'

import { requestWizardPath } from '../config'
import { emptyWizardValues, snapshotToWizardValues, toSaveRequest } from '../lib/form-mappers'
import {
  clearDraftFields,
  clearDraftFiles,
  loadDraftFields,
  loadDraftFiles,
  saveDraftFields,
  saveDraftFiles,
} from '../lib/request-draft'
import { mapServerErrors } from '../lib/server-errors'
import { requestWizardSchema } from '../model/schema'
import { fieldsOfStep, requestWizardSteps, stepOfField } from '../model/steps'
import { type WizardDraft, type WizardLocalFiles, type WizardValues } from '../model/types'
import { CategoryPicker } from './category-picker'
import { CityPicker } from './city-picker'
import { DistrictPicker } from './district-picker'
import { FilesField, SavedFilesField } from './files-field'
import { WarehousePicker } from './warehouse-picker'

/** Заявка уходит всем подходящим фулфилментам, поэтому Мегамаркет в списке площадок не нужен. */
const requestMarketplaces = ['wildberries', 'ozon', 'yandex_market', 'other'] as const

/** Столько же файлов принимает бэкенд: лишние отсекаем сразу, чтобы не грузить их впустую. */
const maxFileCount = 5

/**
 * Признак «файлы черновика не сохранились». Лежит рядом с самим черновиком, а не в состоянии
 * страницы: узнать о потере нужно после возвращения, когда страница уже открылась заново.
 */
const draftFilesLostKey = 'fulfillment-request-draft-files-lost-v1'

const draftRestoredNotice = 'Мы сохранили заполненное — проверьте и отправьте заявку'
const draftFilesLostNotice = 'Мы сохранили заполненное, но файлы придётся выбрать ещё раз'

/** Две отгрузки в день имеют смысл только там, где заказ уезжает покупателю сразу. */
const hasFastShipment = (shipmentTypes: FulfillmentRequestApi.ShipmentType[]) =>
  shipmentTypes.includes('fbs') || shipmentTypes.includes('dbs')

/**
 * Бэкенд сопоставляет федеральные округа только для FBS, а остальные схемы — по городам.
 * Это постоянное устройство, а не переходное состояние: перевода DBS и FBO на округа не будет
 * (решение владельца продукта, подтверждено бэкендом 11.08.2026). Разветвление убирать не нужно.
 */
const needsFederalDistricts = (shipmentTypes: FulfillmentRequestApi.ShipmentType[]) => shipmentTypes.includes('fbs')

const sendButtonLabel = (mode: 'create' | 'edit', isSending: boolean, isSaved: boolean, isGuest: boolean) => {
  if (isSending) {
    return 'Отправляем…'
  }
  if (mode === 'edit') {
    return 'Отправить на повторную проверку'
  }
  if (isSaved) {
    return 'Перейти к заявкам'
  }

  return isGuest ? 'Зарегистрироваться и отправить' : 'Отправить заявку'
}

/** Заявка создана и ждёт проверки, потерялись только вложения — объясняем это отдельно от отказа. */
const filesLostMessage = (fileCount: number) =>
  fileCount === 1
    ? 'Заявка создана, но файл приложить не удалось. Попробуйте загрузить его ещё раз.'
    : 'Заявка создана, но файлы приложить не удалось. Попробуйте загрузить их ещё раз.'

const districtReselectionError = 'Выберите федеральные округа заново'
const districtsLoadError = 'Федеральные округа не загрузились. Повторите загрузку и попробуйте ещё раз'
const districtsLoadingError = 'Дождитесь загрузки федеральных округов'
const districtsRequiredError = 'Выберите хотя бы один федеральный округ'

type VisibleErrorField = keyof WizardValues | 'root.serverError'

type Props = { mode: 'create' } | { mode: 'edit'; snapshot: FulfillmentRequestApi.EditSnapshot }

export const FulfillmentRequestWizard = observer((props: Props) => {
  const router = useRouter()
  const appStore = useAppStore()
  const createRequest = useMutation(fulfillmentRequestCreateMutation())
  const editRequest = useMutation(fulfillmentRequestEditMutation())
  const form = useForm<WizardValues>({
    mode: 'onBlur',
    resolver: yupResolver(requestWizardSchema) as never,
    defaultValues: props.mode === 'edit' ? snapshotToWizardValues(props.snapshot) : emptyWizardValues,
  })
  const {
    clearErrors,
    getFieldState,
    getValues,
    register,
    reset,
    setError: setFormError,
    setValue,
    trigger,
    watch,
  } = form
  const [step, setStep] = useState(0)
  const isSubmitRunning = useRef(false)
  const values = watch()
  const [files, setFiles] = useState<WizardLocalFiles>([])
  const needsDistricts = needsFederalDistricts(values.shipmentTypes)
  const {
    districts: federalDistricts,
    isLoading: isDistrictsLoading,
    hasLoadError: hasDistrictsLoadError,
    retry: retryDistricts,
  } = useFederalDistricts(needsDistricts)
  const knownDistrictCodes = federalDistricts ? new Set(federalDistricts.map((district) => district.code)) : undefined
  const selectedDistrictCodes = knownDistrictCodes
    ? values.federalDistricts.filter((code) => knownDistrictCodes.has(code))
    : values.federalDistricts
  const needsDistrictReselection = selectedDistrictCodes.length !== values.federalDistricts.length
  const [visibleErrorField, setVisibleErrorField] = useState<VisibleErrorField | null>(null)
  const [visibleError, setVisibleError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  /** Текст плашки о восстановленном черновике: висит до отправки, чтобы не потерялся на шагах. */
  const [draftNotice, setDraftNotice] = useState<string | null>(null)
  /** Заявка уже на сервере: повторная отправка завела бы её второй раз. */
  const [isSaved, setIsSaved] = useState(false)
  /** Идентификатор созданной заявки — нужен, чтобы доложить к ней файлы. */
  const [savedRequestId, setSavedRequestId] = useState<string | null>(null)
  /** Файлы, которые ещё не доехали до заявки; повтор берёт только их. */
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  /** Текст плашки про файлы: появляется только когда они действительно не приложились. */
  const [filesNotice, setFilesNotice] = useState<string | null>(null)
  /** Конфликт версии не считается ошибкой поля: введённое остаётся до явного перечитывания. */
  const [hasEditConflict, setHasEditConflict] = useState(false)
  const [needsLoginAfterEdit, setNeedsLoginAfterEdit] = useState(false)

  const set = <Field extends FieldPath<WizardValues>>(key: Field, value: FieldPathValue<WizardValues, Field>) =>
    setValue(key, value, { shouldDirty: true })

  // Черновик создания подхватываем только в его мастере: правка всегда начинается со снимка сервера.
  useEffect(() => {
    if (props.mode !== 'create') {
      return
    }

    const saved = loadDraftFields<WizardDraft>()
    if (saved === null || typeof saved.values !== 'object') {
      return
    }
    let isCurrentPage = true
    reset({ ...emptyWizardValues, ...saved.values })
    setFiles([])
    // Шаг из черновика проверяем на число: испорченная запись иначе увела бы за границы мастера.
    setStep(Number.isInteger(saved.step) ? Math.min(Math.max(saved.step, 0), requestWizardSteps.length - 1) : 0)

    const filesWereLost = localStorage.getItem(draftFilesLostKey) === '1'
    localStorage.removeItem(draftFilesLostKey)
    if (filesWereLost) {
      setDraftNotice(draftFilesLostNotice)

      return
    }

    Promise.resolve(loadDraftFiles())
      .then((files) => {
        if (!isCurrentPage) {
          return
        }
        setFiles(files ?? [])
        setDraftNotice(draftRestoredNotice)
      })
      .catch(() => {
        if (isCurrentPage) {
          setDraftNotice(draftFilesLostNotice)
        }
      })

    return () => {
      isCurrentPage = false
    }
  }, [props.mode, reset])

  const forgetDraft = () => {
    clearDraftFields()
    localStorage.removeItem(draftFilesLostKey)
    void Promise.resolve(clearDraftFiles()).catch(() => undefined)
  }

  /**
   * Сохраняем всё введённое и уводим на регистрацию: заявку нужно к кому-то привязать, иначе
   * ответы фулфилментов будет некуда прислать. После регистрации посетитель вернётся на этот же
   * шаг с заполненными полями.
   */
  const keepDraftAndAskToRegister = async () => {
    saveDraftFields({ values: getValues(), step } satisfies WizardDraft)
    localStorage.removeItem(draftFilesLostKey)
    // Не сохранились файлы — прежние тоже убираем: иначе в заявку уехал бы старый набор,
    // а посетитель считал бы, что приложил новый.
    await Promise.resolve(saveDraftFiles(files)).catch(async () => {
      await Promise.resolve(clearDraftFiles()).catch(() => undefined)
      localStorage.setItem(draftFilesLostKey, '1')
    })
    window.location.href = `/auth/register?callbackUrl=${encodeURIComponent(requestWizardPath)}`
  }

  const contactValue = (messenger: FulfillmentRequestApi.Messenger) =>
    values.contacts.find((contact) => contact.messenger === messenger)?.value

  const toggleContact = (messenger: FulfillmentRequestApi.Messenger) => {
    const contacts = getValues('contacts')
    const isChosen = contacts.some((contact) => contact.messenger === messenger)
    if (isChosen) {
      set(
        'contacts',
        contacts.filter((contact) => contact.messenger !== messenger)
      )

      return
    }

    // По умолчанию подставляем телефон: чаще всего мессенджер на том же номере.
    set('contacts', [...contacts, { messenger, value: getValues('phone').trim() }])
  }

  const setContactValue = (messenger: FulfillmentRequestApi.Messenger, value: string) => {
    const contacts = getValues('contacts')
    set(
      'contacts',
      contacts.map((contact) => (contact.messenger === messenger ? { ...contact, value } : contact))
    )
  }

  const isLastStep = step === requestWizardSteps.length - 1
  const isGuest = !appStore.isLoggedIn
  const stepDescription =
    step === 2 && needsDistricts ? 'Округа, куда нужно возить, и склады отгрузки' : requestWizardSteps[step].description

  type InvalidField = { field: keyof WizardValues; message: string }

  const validateSchemaFields = async (fields: (keyof WizardValues)[]): Promise<InvalidField | null> => {
    const isValid = await trigger(fields)
    if (isValid) {
      return null
    }

    const field = fields.find((item) => getFieldState(item).invalid) ?? fields[0]
    const message = getFieldState(field).error?.message ?? ''
    setVisibleErrorField(field)
    setVisibleError(message)

    return { field, message }
  }

  /** Шаг отгрузки ждёт справочник и требует округ только при FBS. */
  const validateDistrictsStep = async (): Promise<InvalidField | null> => {
    // Предел складов действует при любой схеме работы, поэтому проверяется первым: ветка про
    // округа при FBS возвращает ответ раньше, и до общих проверок шага дело бы не дошло.
    const warehouseError = await validateSchemaFields(['warehouses'])
    if (warehouseError) {
      return warehouseError
    }
    if (!needsDistricts) {
      clearErrors('federalDistricts')

      return null
    }
    // Уже сделанный выбор важнее состояния справочника: коды достаточны для отправки заявки.
    if (selectedDistrictCodes.length > 0 && !needsDistrictReselection) {
      clearErrors('federalDistricts')

      return null
    }

    const message = needsDistrictReselection
      ? districtReselectionError
      : hasDistrictsLoadError
        ? districtsLoadError
        : !federalDistricts
          ? districtsLoadingError
          : districtsRequiredError
    setFormError('federalDistricts', { message })
    setVisibleErrorField('federalDistricts')
    setVisibleError(message)

    return { field: 'federalDistricts', message }
  }

  /** Проверка одного шага: шаг отгрузки идёт своей веткой, остальные проверяет Yup. */
  const invalidFieldAt = (index: number) =>
    index === 2 ? validateDistrictsStep() : validateSchemaFields(fieldsOfStep(index))

  /** Черновик мог сохраниться до появления новых проверок, поэтому перед отправкой проверяем все шаги. */
  const firstInvalidStep = async (): Promise<{ step: number; error: InvalidField } | null> => {
    for (let index = 0; index < requestWizardSteps.length; ++index) {
      const error = await invalidFieldAt(index)
      if (error !== null) {
        return { step: index, error }
      }
    }

    return null
  }

  const goForward = async () => {
    const invalidField = await invalidFieldAt(step)
    if (invalidField === null) {
      setVisibleErrorField(null)
      setVisibleError(null)
      setStep((current) => Math.min(current + 1, requestWizardSteps.length - 1))
    }
  }

  const goToRequests = () => {
    router.push('/fulfillment/requests')
    router.refresh()
  }

  /**
   * Прикладывает файлы по очереди и возвращает те, что не доехали. Повтор начинается ровно с них:
   * иначе уже приложенный файл лёг бы в заявку второй раз, а лишние вложения удалить некому.
   */
  const attachFiles = async (requestId: string, files: File[]): Promise<File[]> => {
    for (let index = 0; index < files.length; ++index) {
      try {
        await fulfillmentRequestApi.attachFile(requestId, files[index])
      } catch {
        return files.slice(index)
      }
    }

    return []
  }

  const showEditError = (field: VisibleErrorField, message: string) => {
    setFormError(field, { message })
    setVisibleErrorField(field)
    setVisibleError(message)
    setStep(stepOfField(field))
  }

  const handleEditFailure = (exception: unknown) => {
    const failureKind = parseFulfillmentRequestFailureKind(exception)
    setNeedsLoginAfterEdit(failureKind === 'unauthorized')
    if (failureKind === 'conflict') {
      setHasEditConflict(true)

      return
    }

    if (failureKind === 'validation') {
      const serverErrors = axios.isAxiosError(exception) ? exception.response?.data?.errors : undefined
      const mappedErrors = mapServerErrors(serverErrors)
      if (mappedErrors.length > 0) {
        for (const error of mappedErrors) {
          setFormError(error.field, { message: error.message })
        }
        const firstErrorStep = Math.min(...mappedErrors.map((error) => stepOfField(error.field)))
        const firstError = mappedErrors.find((error) => stepOfField(error.field) === firstErrorStep)!
        setVisibleErrorField(firstError.field)
        setVisibleError(firstError.message)
        setStep(firstErrorStep)

        return
      }
    }

    const messageByFailure = {
      validation: 'Сервер не принял заявку. Проверьте выделенные поля и отправьте её ещё раз.',
      unauthorized: 'Сессия истекла. Войдите заново, откройте заявку и повторите отправку.',
      forbidden: 'Эта заявка принадлежит другому пользователю. Вернитесь в список своих заявок.',
      'not-found': 'Заявка не найдена. Вернитесь в список своих заявок и проверьте, осталась ли она там.',
      'too-many-requests': 'Слишком много попыток. Подождите несколько минут и отправьте заявку ещё раз.',
      unknown: 'Сервис временно недоступен. Проверьте соединение и попробуйте сохранить заявку позже.',
    } as const
    showEditError('root.serverError', messageByFailure[failureKind])
  }

  const submit = async () => {
    if (isSubmitRunning.current) {
      return
    }
    isSubmitRunning.current = true

    if (isSaved) {
      goToRequests()
      isSubmitRunning.current = false

      return
    }

    const invalidStep = await firstInvalidStep()
    if (invalidStep !== null) {
      // Единственная ошибка, ради которой никуда не уводим: справочник округов ещё едет, и на шаге
      // отгрузки выбирать пока нечего. Все остальные требуют вернуться туда, где их можно исправить.
      if (invalidStep.error.message !== districtsLoadingError) {
        setStep(invalidStep.step)
      }
      isSubmitRunning.current = false

      return
    }

    setVisibleErrorField(null)
    setVisibleError(null)
    setHasEditConflict(false)
    setNeedsLoginAfterEdit(false)
    setIsSending(true)
    if (props.mode === 'edit') {
      try {
        await editRequest.mutateAsync({
          id: props.snapshot.id,
          request: {
            ...toSaveRequest(getValues(), federalDistricts),
            expected_revision: props.snapshot.revision,
          },
        })
        goToRequests()
      } catch (exception) {
        handleEditFailure(exception)
      } finally {
        setIsSending(false)
        isSubmitRunning.current = false
      }

      return
    }

    let isCreated = false
    try {
      const createdRequest = await createRequest.mutateAsync(toSaveRequest(getValues(), federalDistricts))
      const requestId = createdRequest.id
      // Заявка создана и ждёт проверки: дальше остаётся доложить файлы, но не отправить её ещё раз.
      isCreated = true
      setIsSaved(true)
      forgetDraft()
      // Файлы прикладываем к уже созданной заявке: до неё их некуда привязать.
      setSavedRequestId(requestId)

      const notAttached = await attachFiles(requestId, files)
      setPendingFiles(notAttached)
      if (notAttached.length === 0) {
        goToRequests()

        return
      }
      setFilesNotice(filesLostMessage(notAttached.length))
    } catch (exception) {
      if (isCreated) {
        return
      }
      // Кто отправляет заявку, решает сервер: локальное состояние после входа в соседней вкладке
      // ещё считает посетителя гостем, а после неудачного запроса о нём — наоборот. Поэтому на
      // регистрацию уводит именно ответ 401, а не наша догадка.
      if (axios.isAxiosError(exception) && exception.response?.status === 401) {
        await keepDraftAndAskToRegister()

        return
      }
      const message = 'Не удалось отправить заявку. Проверьте поля и попробуйте ещё раз'
      setFormError('root.serverError', { message })
      setVisibleErrorField('root.serverError')
      setVisibleError(message)
    } finally {
      setIsSending(false)
      isSubmitRunning.current = false
    }
  }

  const retryFiles = async () => {
    if (savedRequestId === null) {
      return
    }

    setIsSending(true)
    try {
      const notAttached = await attachFiles(savedRequestId, pendingFiles)
      setPendingFiles(notAttached)
      if (notAttached.length === 0) {
        setFilesNotice(null)
        goToRequests()

        return
      }
      setFilesNotice(
        notAttached.length === 1
          ? 'Файл снова не загрузился. Попробуйте ещё раз.'
          : 'Файлы снова не загрузились. Попробуйте ещё раз.'
      )
    } finally {
      setIsSending(false)
    }
  }

  return (
    <form onSubmit={(event) => event.preventDefault()} noValidate>
      <Card>
        <CardHeader className="border-b">
          <CardTitle>
            Шаг {step + 1} из {requestWizardSteps.length}: {requestWizardSteps[step].title}
          </CardTitle>
          <CardDescription>{stepDescription}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          {step === 0 && (
            <>
              <Field>
                <FieldLabel htmlFor="request-title">Что нужно сделать</FieldLabel>
                <Input
                  id="request-title"
                  {...register('title')}
                  maxLength={160}
                  placeholder="Например, приёмка и упаковка футболок"
                />
              </Field>
              <div role="group" aria-label="Схемы работы" className="grid gap-2.5">
                <span className={typeClass.blockTitle}>Схемы работы</span>
                {requestShipmentTypes.map(({ code, label }) => (
                  <Label key={code} htmlFor={`request-shipment-${code}`} className="cursor-pointer">
                    <Checkbox
                      id={`request-shipment-${code}`}
                      checked={values.shipmentTypes.includes(code)}
                      onCheckedChange={() =>
                        set(
                          'shipmentTypes',
                          toggleValue(values.shipmentTypes, code) as FulfillmentRequestApi.ShipmentType[]
                        )
                      }
                    />
                    <span>{label}</span>
                  </Label>
                ))}
              </div>
              <div role="group" aria-label="Маркетплейсы" className="grid gap-2.5">
                <span className={typeClass.blockTitle}>Маркетплейсы</span>
                {requestMarketplaces.map((marketplace) => (
                  <Label key={marketplace} htmlFor={`request-marketplace-${marketplace}`} className="cursor-pointer">
                    <Checkbox
                      id={`request-marketplace-${marketplace}`}
                      checked={values.marketplaces.includes(marketplace)}
                      onCheckedChange={() => set('marketplaces', toggleValue(values.marketplaces, marketplace))}
                    />
                    <MarketplaceTag
                      marketplace={marketplace as FulfillmentApi.Marketplace}
                      label={marketplaceLabels[marketplace as FulfillmentApi.Marketplace]}
                    />
                  </Label>
                ))}
                {values.marketplaces.includes('other') && (
                  <Field className="pt-1">
                    <FieldLabel htmlFor="request-marketplace-other-name">Какая площадка</FieldLabel>
                    <Input
                      id="request-marketplace-other-name"
                      {...register('marketplaceOtherName')}
                      value={values.marketplaceOtherName}
                      maxLength={160}
                      placeholder="Например, Лемана Про"
                      onChange={(event) => set('marketplaceOtherName', event.target.value)}
                    />
                  </Field>
                )}
              </div>
            </>
          )}

          {step === 1 && <CityPicker cities={values.cities} onChange={(cities) => set('cities', cities)} />}

          {step === 2 && (
            <>
              {needsDistricts && (
                <DistrictPicker
                  districts={federalDistricts}
                  selectedCodes={selectedDistrictCodes}
                  isLoading={isDistrictsLoading}
                  hasRequestError={hasDistrictsLoadError}
                  hasValidationError={visibleErrorField === 'federalDistricts' && Boolean(visibleError)}
                  needsReselection={needsDistrictReselection}
                  onChange={(codes) => set('federalDistricts', codes)}
                  onRetry={() => {
                    clearErrors('federalDistricts')
                    setVisibleErrorField(null)
                    setVisibleError(null)
                    retryDistricts()
                  }}
                />
              )}
              <WarehousePicker
                warehouses={values.warehouses}
                onChange={(warehouses) => set('warehouses', warehouses)}
              />
            </>
          )}

          {step === 3 && (
            <>
              <CategoryPicker
                subjectName={values.subjectName}
                onChange={(category) => {
                  set('subjectId', category.subjectId)
                  set('subjectName', category.subjectName)
                }}
              />
              <Field>
                <FieldLabel htmlFor="request-product-url">Ссылка на товар, если он уже продаётся</FieldLabel>
                <Input
                  id="request-product-url"
                  type="url"
                  {...register('productUrl')}
                  placeholder="https://www.wildberries.ru/catalog/…"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="request-product-size">Габариты товара без упаковки</FieldLabel>
                <Input
                  id="request-product-size"
                  {...register('productSize')}
                  maxLength={120}
                  placeholder="Например, 200x300x100 мм"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="request-package-size">Габариты упаковки</FieldLabel>
                <Input
                  id="request-package-size"
                  {...register('packageSize')}
                  maxLength={120}
                  placeholder="Например, 220x320x120 мм"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="request-unit-count">Количество единиц товара</FieldLabel>
                <Input
                  id="request-unit-count"
                  inputMode="numeric"
                  {...register('unitCount')}
                  value={values.unitCount}
                  placeholder="Например, 500"
                  onChange={(event) =>
                    setValue('unitCount', event.target.value.replace(/\D/g, ''), { shouldDirty: true })
                  }
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="request-due-date">Когда нужно выполнить</FieldLabel>
                <Input id="request-due-date" type="date" {...register('dueDate')} />
              </Field>
            </>
          )}

          {step === 4 && (
            <>
              <div role="group" aria-label="Нужные услуги" className="grid gap-2.5">
                <span className={typeClass.blockTitle}>Нужные услуги</span>
                {serviceTypes.map(({ code, label }) => (
                  <Label key={code} htmlFor={`request-service-${code}`} className="cursor-pointer">
                    <Checkbox
                      id={`request-service-${code}`}
                      checked={values.services.includes(code)}
                      onCheckedChange={() => set('services', toggleValue(values.services, code))}
                    />
                    <span>{label}</span>
                  </Label>
                ))}
              </div>
              <div className="grid gap-2.5">
                <span className={typeClass.blockTitle}>Во что паковать</span>
                <RadioGroup
                  value={values.packingType ?? ''}
                  onValueChange={(value) => set('packingType', value as FulfillmentRequestApi.PackingType)}
                  aria-label="Во что паковать"
                  className="grid gap-2"
                >
                  {packingTypes.map(({ code, label }) => (
                    <Label key={code} htmlFor={`request-packing-${code}`} className="cursor-pointer">
                      <RadioGroupItem id={`request-packing-${code}`} value={code} />
                      <span>{label}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
              {values.packingType === 'other' && (
                <Field>
                  <FieldLabel htmlFor="request-packing-comment">Как именно паковать</FieldLabel>
                  <Input
                    id="request-packing-comment"
                    {...register('packingComment')}
                    maxLength={500}
                    placeholder="Например, картонные тубусы"
                  />
                </Field>
              )}
              <Label htmlFor="request-special-packing" className="cursor-pointer">
                <Checkbox
                  id="request-special-packing"
                  checked={values.needsSpecialPacking}
                  onCheckedChange={() => set('needsSpecialPacking', !values.needsSpecialPacking)}
                />
                <span>Нужна особая упаковка</span>
              </Label>
              {values.needsSpecialPacking && (
                <Field>
                  <FieldLabel htmlFor="request-special-packing-text">Что за упаковка</FieldLabel>
                  <Textarea
                    id="request-special-packing-text"
                    {...register('specialPacking')}
                    value={values.specialPacking}
                    rows={2}
                    maxLength={500}
                    placeholder="Например, термоусадка, вкладыш с инструкцией, бирка на каждой единице"
                    onChange={(event) => set('specialPacking', event.target.value)}
                    // Ошибка шага показывается общей плашкой внизу: связываем её с полем, иначе
                    // человек со средством чтения с экрана не поймёт, о каком поле речь.
                    aria-invalid={visibleErrorField === 'specialPacking' && Boolean(visibleError)}
                    aria-describedby={
                      visibleErrorField === 'specialPacking' && visibleError ? 'request-wizard-error' : undefined
                    }
                  />
                </Field>
              )}
              {hasFastShipment(values.shipmentTypes) && (
                <Label htmlFor="request-twice-a-day" className="cursor-pointer">
                  <Checkbox
                    id="request-twice-a-day"
                    checked={values.needsTwiceADay}
                    onCheckedChange={() => set('needsTwiceADay', !values.needsTwiceADay)}
                  />
                  <span>Нужны две отгрузки в день — заявку увидят только такие фулфилменты</span>
                </Label>
              )}
              <Field>
                <FieldLabel htmlFor="request-description">Описание задачи</FieldLabel>
                <Textarea
                  id="request-description"
                  {...register('description')}
                  rows={4}
                  maxLength={4000}
                  placeholder="Что за товар, как он приходит, что должно получиться на выходе"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="request-requirements">Особые требования</FieldLabel>
                <Textarea
                  id="request-requirements"
                  {...register('requirements')}
                  rows={3}
                  maxLength={2000}
                  placeholder="Например, отдельная зона хранения или фотоотчёт по каждой партии"
                />
              </Field>
            </>
          )}

          {step === 5 && (
            <>
              <Field>
                <FieldLabel htmlFor="request-phone">Телефон для связи</FieldLabel>
                <Input id="request-phone" type="tel" {...register('phone')} placeholder="+7 900 123-45-67" />
              </Field>
              <div role="group" aria-label="Мессенджеры" className="grid gap-2.5">
                <span className={typeClass.blockTitle}>Мессенджеры</span>
                {messengers.map(({ code, label }) => {
                  const chosen = contactValue(code)
                  return (
                    <div key={code} className="grid gap-2">
                      <Label htmlFor={`request-messenger-${code}`} className="cursor-pointer">
                        <Checkbox
                          id={`request-messenger-${code}`}
                          checked={chosen !== undefined}
                          onCheckedChange={() => toggleContact(code)}
                        />
                        <span>{label}</span>
                      </Label>
                      {chosen !== undefined && (
                        <Input
                          aria-label={`Значение для ${label}`}
                          value={chosen}
                          maxLength={120}
                          placeholder="Номер или имя пользователя"
                          onChange={(event) => setContactValue(code, event.target.value)}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
              {props.mode === 'edit' ? (
                <SavedFilesField requestId={props.snapshot.id} files={props.snapshot.files} />
              ) : (
                <FilesField
                  files={files}
                  maxCount={maxFileCount}
                  onChange={setFiles}
                  // После создания заявки грузятся ровно эти файлы: правка списка разошлась бы с тем,
                  // что уже отправлено, и повтор всё равно ушёл бы со старым набором.
                  isLocked={isSaved}
                />
              )}
            </>
          )}

          {props.mode === 'create' && draftNotice && !isSaved && (
            <Alert variant="success" role="status">
              <AlertTitle>Заявка на месте</AlertTitle>
              <AlertDescription>{draftNotice}</AlertDescription>
            </Alert>
          )}

          {props.mode === 'create' && isLastStep && isGuest && !isSaved && (
            <Alert variant="info" role="note">
              <AlertTitle>Остался последний шаг — учётная запись</AlertTitle>
              <AlertDescription>
                Заявку нужно к кому-то привязать: без неё ответы фулфилментов будет некуда прислать. Регистрация
                бесплатна и занимает минуту, а всё заполненное сохранится и дождётся вас здесь.
              </AlertDescription>
            </Alert>
          )}

          {props.mode === 'create' && filesNotice && (
            <Alert variant="warning">
              <AlertTitle>Заявка создана</AlertTitle>
              <AlertDescription>
                {filesNotice}
                {pendingFiles.length > 0 && savedRequestId !== null && (
                  <div className="mt-3">
                    <Button type="button" variant="outline" disabled={isSending} onClick={() => void retryFiles()}>
                      Повторить загрузку файлов
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {visibleError && (
            <Alert variant="destructive" role="alert" id="request-wizard-error">
              <AlertTitle>Проверьте заполнение</AlertTitle>
              <AlertDescription>
                <FieldError>{visibleError}</FieldError>
                {needsLoginAfterEdit && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        window.location.href = `/auth/login?callbackUrl=${encodeURIComponent(window.location.pathname)}`
                      }}
                    >
                      Войти заново
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {hasEditConflict && (
            <Alert variant="warning" role="alert">
              <AlertTitle>Заявка изменилась</AlertTitle>
              <AlertDescription>
                <p>Заявку уже разобрали или она изменилась. Проверьте свежие данные перед новой отправкой.</p>
                <div className="mt-3">
                  <Button type="button" variant="outline" onClick={() => window.location.reload()}>
                    Обновить страницу
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
        <WizardFooter
          percent={Math.round(((step + 1) / requestWizardSteps.length) * 100)}
          nextLabel={isLastStep ? sendButtonLabel(props.mode, isSending, isSaved, isGuest) : 'Далее'}
          isNextDisabled={isLastStep && isSending}
          onBack={step === 0 ? undefined : () => setStep((current) => current - 1)}
          onNext={isLastStep ? () => void submit() : () => void goForward()}
        />
      </Card>
    </form>
  )
})
